import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Loader2,
  Send,
  Mail,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Eye,
  Code2,
  ChevronRight,
  History,
  Wand2,
  Inbox,
  Upload,
  ArrowUp,
  ArrowDown,
  X,
  Pencil,
  Database,
  Save,
  FileText,
  BarChart2,
  Repeat,
  Trash2,
  Palette,
  Users,
  UserCheck,
  Zap,
  Search,
  LayoutGrid,
  List as ListIcon,
  ShieldAlert,
  Download,
  Image as ImageIcon,
  UploadCloud,
  Link2,
  UserPlus,
  LayoutTemplate,
  Plus,
  Maximize2,
} from "lucide-react";
import { API, CATEGORY_SLUGS, categoryLabel, resolveImage } from "../products";
import { TEMPLATES, renderTemplate, defaultSubject, extractProductImages } from "../flyerTemplates";

const TOKEN_KEY = "tcs_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const jsonHeaders = () => ({ ...authHeaders(), "Content-Type": "application/json" });

function detectHeaderRow(preview) {
  if (!preview || preview.length === 0) return 0;
  let best = 0;
  let bestCount = -1;
  preview.forEach((row, i) => {
    const filled = row.filter((c) => String(c || "").trim()).length;
    if (filled > bestCount) { bestCount = filled; best = i; }
  });
  return best;
}

function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDone, 4000);
    return () => clearTimeout(id);
  }, [toast, onDone]);
  if (!toast) return null;
  const ok = toast.type !== "error";
  return (
    <div
      data-testid="marketing-toast"
      className={`fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-5 py-3 rounded-xl shadow-2xl text-white font-semibold max-w-md ${
        ok ? "bg-green-600" : "bg-red-600"
      }`}
    >
      {ok ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
      <span className="text-sm">{toast.msg}</span>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="text-center">
    <div className={`text-lg font-extrabold ${accent ? "text-green-600" : "text-slate-800"}`}>{value ?? 0}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
  </div>
);

const ColorField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded-md border border-slate-300 cursor-pointer p-0.5 bg-white" data-testid={`${testid}-picker`} />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="form-input flex-1 font-mono text-xs" data-testid={testid} />
    </div>
  </div>
);

const StepBadge = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <span
      className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
        done || active ? "bg-green-600 text-white" : "bg-slate-200 text-slate-500"
      }`}
    >
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </span>
    <span className={`text-sm font-bold ${active || done ? "text-slate-800" : "text-slate-400"}`}>{label}</span>
  </div>
);

// ---- Upload-sheet source picker ----
function SheetSource({ notify, onLoaded }) {
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheet, setSheet] = useState("");
  const [headerRow, setHeaderRow] = useState(0);
  const [category, setCategory] = useState("laptops");
  const [inspecting, setInspecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const current = sheets.find((s) => s.name === sheet);

  const onPick = async (f) => {
    if (!f) return;
    setFile(f);
    setInspecting(true);
    setSheets([]);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await fetch(`${API}/admin/products/inspect`, { method: "POST", headers: authHeaders(), body: fd });
      if (!res.ok) throw new Error("inspect");
      const data = await res.json();
      setSheets(data.sheets || []);
      const first = (data.sheets || []).find((s) => s.preview.length > 0) || data.sheets?.[0];
      setSheet(first?.name || "");
      setHeaderRow(detectHeaderRow(first?.preview));
    } catch (e) {
      notify("error", "Could not read file");
    } finally {
      setInspecting(false);
    }
  };

  const loadProducts = async () => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("sheet", sheet);
    fd.append("default_category", category);
    fd.append("header_row", headerRow);
    try {
      const res = await fetch(`${API}/admin/products/parse`, { method: "POST", headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "parse");
      onLoaded(data.products || []);
      notify("success", `Loaded ${data.total} products from sheet`);
    } catch (e) {
      notify("error", typeof e.message === "string" ? e.message : "Could not parse sheet");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="marketing-sheet-source">
      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-6 cursor-pointer hover:border-green-400 transition-colors">
        <Upload className="w-6 h-6 text-slate-400" />
        <span className="text-sm font-semibold text-slate-600">{file ? file.name : "Choose a .xlsx or .csv file (not added to your catalog)"}</span>
        <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid="marketing-sheet-input" />
      </label>

      {inspecting && (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Reading file…</div>
      )}

      {sheets.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Sheet / tab</label>
              <select value={sheet} onChange={(e) => { setSheet(e.target.value); setHeaderRow(detectHeaderRow(sheets.find((s) => s.name === e.target.value)?.preview)); }} className="form-input" data-testid="marketing-sheet-select">
                {sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="form-input" data-testid="marketing-sheet-category">
                {CATEGORY_SLUGS.map((s) => <option key={s} value={s}>{categoryLabel(s, "en")}</option>)}
              </select>
            </div>
          </div>
          {current && current.preview.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Click the row that contains your column headers:</p>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="text-xs w-full">
                  <tbody>
                    {current.preview.map((row, i) => (
                      <tr key={i} onClick={() => setHeaderRow(i)} data-testid={`marketing-preview-row-${i}`}
                        className={`cursor-pointer border-b border-slate-100 last:border-0 ${headerRow === i ? "bg-green-50 ring-1 ring-inset ring-green-400" : "hover:bg-slate-50"}`}>
                        <td className="px-2 py-1.5 text-slate-400 font-mono whitespace-nowrap">{headerRow === i ? "▶ header" : `row ${i}`}</td>
                        {row.slice(0, 10).map((c, j) => (
                          <td key={j} className="px-2 py-1.5 text-slate-700 whitespace-nowrap max-w-[140px] truncate">{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <button onClick={loadProducts} disabled={loading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm disabled:opacity-60" data-testid="marketing-load-sheet-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Load products from sheet
          </button>
        </>
      )}
    </div>
  );
}

// ---- Main Marketing Studio ----
export default function AdminMarketing({ onUnauthorized }) {
  const [source, setSource] = useState("inventory"); // inventory | upload
  const [products, setProducts] = useState([]); // live inventory
  const [mktStage, setMktStage] = useState("finished"); // finished | in_process | all
  const [pickView, setPickView] = useState("list"); // list | grid
  const [pickSearch, setPickSearch] = useState("");
  const [pickCat, setPickCat] = useState("all");
  const [parsed, setParsed] = useState([]); // uploaded sheet products
  const [selected, setSelected] = useState([]); // ordered ids
  const [lang, setLang] = useState("en");
  const [templateId, setTemplateId] = useState("grid");
  const [generating, setGenerating] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [fullPreview, setFullPreview] = useState(false);
  const [brand, setBrand] = useState({
    logo_style: "auto",
    header_bg: "#0f172a",
    accent_color: "#16a34a",
    text_color: "#1f2937",
    footer_bg: "#111827",
    footer_text: "#e2e8f0",
  });
  const setBrandField = (k, v) => setBrand((b) => ({ ...b, [k]: v }));

  // image-swap state
  const [swapSrc, setSwapSrc] = useState(null); // the src currently being replaced
  const [swapTab, setSwapTab] = useState("upload"); // upload | url | search
  const [swapUrl, setSwapUrl] = useState("");
  const [swapQuery, setSwapQuery] = useState("");
  const [swapResults, setSwapResults] = useState([]);
  const [swapBusy, setSwapBusy] = useState(false);

  // manual add-contact state
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addListId, setAddListId] = useState("");
  const [addingContact, setAddingContact] = useState(false);
  // create-list state
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [view, setView] = useState("preview"); // preview | edit | code
  const previewRef = useRef(null);

  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [listError, setListError] = useState("");
  const [sender, setSender] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);
  const priceListUrl = `${API}/products/price-list.pdf?lang=${lang}`;

  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [stats, setStats] = useState({});
  const [resending, setResending] = useState("");
  const [signupListId, setSignupListId] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [subPending, setSubPending] = useState(0);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [assignListId, setAssignListId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [deliver, setDeliver] = useState(null); // {blocked, blocked_count, scanned, total_contacts}
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const notify = (type, msg) => setToast({ type, msg });

  const inventoryPool = mktStage === "all" ? products : products.filter((p) => (p.stage || "finished") === mktStage);
  const basePool = source === "upload" ? parsed : inventoryPool;
  const pool = (() => {
    let list = basePool;
    if (pickCat !== "all") list = list.filter((p) => (p.category || "other") === pickCat);
    const term = pickSearch.trim().toLowerCase();
    if (term) {
      list = list.filter((p) =>
        [p.name, p.brand, p.model, p.category].filter(Boolean).join(" ").toLowerCase().includes(term)
      );
    }
    return list;
  })();
  const selectedLookup = source === "upload" ? parsed : products;
  const selectedProducts = selected.map((id) => selectedLookup.find((p) => p.id === id)).filter(Boolean);

  const loadProducts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/products?limit=1000`, { headers: authHeaders() });
      if (res.status === 401) return onUnauthorized();
      const data = await res.json();
      setProducts((data.items || []).filter((p) => p.active));
    } catch (e) {
      notify("error", "Failed to load products");
    }
  }, [onUnauthorized]);

  const loadLists = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/lists`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { setListError(data.detail || "Could not load Brevo lists"); return; }
      setLists(data.lists || []);
      setSender(data.sender || null);
      setListError("");
      if (data.lists?.length) setListId(String(data.lists[0].id));
    } catch (e) {
      setListError("Could not reach Brevo");
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/history`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setHistory(data.campaigns || []);
    } catch (e) { /* noop */ }
  }, []);

  const loadDrafts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/drafts`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setDrafts(data.drafts || []);
    } catch (e) { /* noop */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/settings`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.signup_list_id) setSignupListId(String(data.signup_list_id));
    } catch (e) { /* noop */ }
  }, []);

  const loadSubscribers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/subscribers`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) { setSubscribers(data.subscribers || []); setSubPending(data.pending || 0); }
    } catch (e) { /* noop */ }
  }, []);

  const loadDeliverability = useCallback(async () => {
    setDeliverLoading(true);
    try {
      const res = await fetch(`${API}/admin/marketing/deliverability`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setDeliver(data);
      else notify("error", data.detail || "Could not load deliverability");
    } catch (e) {
      notify("error", "Could not reach Brevo");
    } finally {
      setDeliverLoading(false);
    }
  }, []);

  const removeBadContact = async (email) => {
    if (!window.confirm(`Permanently delete ${email} from Brevo? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/marketing/contacts?email=${encodeURIComponent(email)}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
      notify("success", `Deleted ${email}`);
      loadDeliverability();
    } catch (e) {
      notify("error", "Could not delete contact");
    }
  };

  const exportBadEmails = async () => {
    try {
      const res = await fetch(`${API}/admin/marketing/deliverability/export`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "bad-emails.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notify("error", "Export failed");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const list = lists.find((l) => String(l.id) === String(signupListId));
      const res = await fetch(`${API}/admin/marketing/settings`, {
        method: "PUT", headers: jsonHeaders(),
        body: JSON.stringify({ signup_list_id: signupListId ? Number(signupListId) : null, signup_list_name: list?.name || "" }),
      });
      if (!res.ok) throw new Error();
      notify("success", "Website signup list saved");
    } catch (e) {
      notify("error", "Could not save signup list");
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => { loadProducts(); loadLists(); loadHistory(); loadDrafts(); loadSettings(); loadSubscribers(); loadDeliverability(); }, [loadProducts, loadLists, loadHistory, loadDrafts, loadSettings, loadSubscribers, loadDeliverability]);

  const toggleSub = (id) => setSelectedSubs((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSubsSelected = subscribers.length > 0 && selectedSubs.length === subscribers.length;
  const toggleAllSubs = () => setSelectedSubs(allSubsSelected ? [] : subscribers.map((s) => s.id));

  const assignSubs = async (useAll) => {
    if (!assignListId) return notify("error", "Pick a list to assign to");
    const ids = useAll ? [] : selectedSubs;
    if (!useAll && ids.length === 0) return notify("error", "Select at least one subscriber");
    setAssigning(true);
    try {
      const list = lists.find((l) => String(l.id) === String(assignListId));
      const res = await fetch(`${API}/admin/marketing/subscribers/assign`, {
        method: "POST", headers: jsonHeaders(),
        body: JSON.stringify({ ids, all: !!useAll, list_id: Number(assignListId), list_name: list?.name || "" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Assign failed");
      notify("success", `Assigned ${data.assigned} subscriber(s) to ${list?.name || "list"}${data.errors ? ` (${data.errors} failed)` : ""}`);
      setSelectedSubs([]);
      loadSubscribers();
    } catch (e) {
      notify("error", e.message || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const deleteSub = async (id) => {
    try {
      await fetch(`${API}/admin/marketing/subscribers/${id}`, { method: "DELETE", headers: authHeaders() });
      setSelectedSubs((s) => s.filter((x) => x !== id));
      loadSubscribers();
    } catch (e) { notify("error", "Could not delete subscriber"); }
  };

  const saveDraft = async () => {
    if (!html) return notify("error", "Generate a flyer first");
    setSavingDraft(true);
    try {
      const res = await fetch(`${API}/admin/marketing/drafts`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify({ name: subject || "Untitled draft", subject, html: currentHtml() }) });
      if (!res.ok) throw new Error();
      notify("success", "Draft saved");
      loadDrafts();
    } catch (e) {
      notify("error", "Could not save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const openDraft = async (id) => {
    try {
      const res = await fetch(`${API}/admin/marketing/drafts/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setSubject(data.subject || "");
      setHtml(data.html || "");
      setView("preview");
      notify("success", "Draft loaded");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      notify("error", "Could not open draft");
    }
  };

  const deleteDraft = async (id) => {
    try {
      await fetch(`${API}/admin/marketing/drafts/${id}`, { method: "DELETE", headers: authHeaders() });
      loadDrafts();
    } catch (e) { notify("error", "Could not delete draft"); }
  };

  const loadStats = async (c) => {
    if (!c.brevo_campaign_id) return;
    try {
      const res = await fetch(`${API}/admin/marketing/campaign/${c.brevo_campaign_id}/stats`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "");
      setStats((s) => ({ ...s, [c.id]: data }));
    } catch (e) {
      notify("error", "Could not load stats");
    }
  };

  const resend = async (c) => {
    setResending(c.id);
    try {
      const res = await fetch(`${API}/admin/marketing/resend`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify({ campaign_id: c.id }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "");
      notify("success", "Reminder campaign sent");
      loadHistory();
    } catch (e) {
      notify("error", e.message || "Resend failed");
    } finally {
      setResending("");
    }
  };

  // toggle source clears selection
  const switchSource = (s) => { setSource(s); setSelected([]); };
  const onSheetLoaded = (rows) => { setParsed(rows); setSelected(rows.map((r) => r.id)); };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const removeSel = (id) => setSelected((s) => s.filter((x) => x !== id));
  const move = (idx, dir) => {
    setSelected((s) => {
      const arr = [...s];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  // ---- visual editor helpers ----
  const applyMode = () => {
    const doc = previewRef.current?.contentDocument;
    if (doc) {
      try { doc.designMode = view === "edit" ? "on" : "off"; } catch (e) { /* noop */ }
    }
  };
  useEffect(() => { applyMode(); }); // re-apply on each render (after view/html change)

  const currentHtml = () => {
    if (view === "edit") {
      const doc = previewRef.current?.contentDocument;
      if (doc) return "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
    }
    return html;
  };
  const changeView = (v) => {
    if (view === "edit" && v !== "edit") setHtml(currentHtml());
    setView(v);
  };

  const buildEmail = () => {
    if (selected.length === 0) { notify("error", "Select at least one product"); return; }
    const html2 = renderTemplate({
      templateId,
      products: selectedProducts,
      brand,
      lang,
      origin: window.location.origin,
    });
    setHtml(html2);
    setSubject(defaultSubject(templateId, selectedProducts, lang));
    setView("preview");
    notify("success", "Email built! Review, restyle & swap images below.");
    setTimeout(() => document.querySelector('[data-testid="marketing-editor"]')?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  // ---- in-place restyle (no rebuild, keeps manual edits) ----
  const isDarkHex = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
    if (!m) return true;
    const n = parseInt(m[1], 16);
    return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 < 0.6;
  };
  const swapLogo = (h, toWhite) =>
    h.replace(/tcs_logo(_white)?\.png/g, toWhite ? "tcs_logo_white.png" : "tcs_logo.png");

  const restyleBrand = (key, val) => {
    setHtml((h) => {
      if (!h) return h;
      let out = h;
      if (key === "logo_style") {
        const toWhite = val === "white" || (val !== "dark" && isDarkHex(brand.header_bg));
        out = swapLogo(out, toWhite);
      } else {
        const old = brand[key];
        if (old && val && old.toLowerCase() !== val.toLowerCase()) {
          out = out.split(old).join(val).split(old.toUpperCase()).join(val).split(old.toLowerCase()).join(val);
        }
        if (key === "header_bg" && brand.logo_style === "auto") {
          out = swapLogo(out, isDarkHex(val));
        }
      }
      return out;
    });
    setBrandField(key, val);
  };

  const resetBrand = () => {
    const def = { logo_style: "auto", header_bg: "#0f172a", accent_color: "#16a34a", text_color: "#1f2937", footer_bg: "#111827", footer_text: "#e2e8f0" };
    Object.entries(def).forEach(([k, v]) => restyleBrand(k, v));
  };

  // ---- image swap ----
  const productImages = extractProductImages(html);
  const absImg = (u) => (u && u.startsWith("/") ? `${API.replace(/\/api$/, "")}${u}` : u);

  const replaceImageInHtml = (newAbs) => {
    if (!swapSrc || !newAbs) return;
    setHtml((h) => h.split(swapSrc).join(newAbs));
    setSwapSrc(null);
    setSwapUrl("");
    setSwapQuery("");
    setSwapResults([]);
    notify("success", "Image updated on the flyer");
  };

  const uploadSwapFile = async (file) => {
    if (!file) return;
    setSwapBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/upload-image`, { method: "POST", headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      replaceImageInHtml(absImg(data.image_url));
    } catch (e) {
      notify("error", e.message || "Upload failed");
    } finally {
      setSwapBusy(false);
    }
  };

  const useSwapUrl = async () => {
    if (!swapUrl.trim()) return;
    setSwapBusy(true);
    try {
      const res = await fetch(`${API}/admin/ai/save-image-from-url`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify({ url: swapUrl.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not use that URL");
      replaceImageInHtml(absImg(data.image_url));
    } catch (e) {
      notify("error", e.message || "Could not use that URL");
    } finally {
      setSwapBusy(false);
    }
  };

  const runImageSearch = async () => {
    if (!swapQuery.trim()) return;
    setSwapBusy(true);
    setSwapResults([]);
    try {
      const res = await fetch(`${API}/admin/ai/image-search?query=${encodeURIComponent(swapQuery.trim())}&limit=16`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Search failed");
      setSwapResults(data.images || []);
    } catch (e) {
      notify("error", e.message || "Search failed");
    } finally {
      setSwapBusy(false);
    }
  };

  const pickSearchResult = async (url) => {
    setSwapBusy(true);
    try {
      const res = await fetch(`${API}/admin/ai/save-image-from-url`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify({ url }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not save image");
      replaceImageInHtml(absImg(data.image_url));
    } catch (e) {
      notify("error", e.message || "Could not save image");
    } finally {
      setSwapBusy(false);
    }
  };

  // ---- manual add contact ----
  const createList = async () => {
    const name = newListName.trim();
    if (!name) return notify("error", "Enter a list name");
    setCreatingList(true);
    try {
      const res = await fetch(`${API}/admin/marketing/lists`, {
        method: "POST", headers: jsonHeaders(), body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not create list");
      notify("success", `List "${name}" created`);
      await loadLists();
      setAddListId(String(data.id));
      setNewListName("");
      setShowNewList(false);
    } catch (e) {
      notify("error", e.message || "Could not create list");
    } finally {
      setCreatingList(false);
    }
  };

  const addContact = async () => {
    if (!addEmail.trim()) return notify("error", "Enter an email address");
    setAddingContact(true);
    try {
      const list = lists.find((l) => String(l.id) === String(addListId));
      const res = await fetch(`${API}/admin/marketing/subscribers/add`, {
        method: "POST", headers: jsonHeaders(),
        body: JSON.stringify({ name: addName.trim(), email: addEmail.trim(), list_id: addListId ? Number(addListId) : null, list_name: list?.name || "" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not add contact");
      notify("success", data.synced ? `Added ${addEmail} to ${list?.name || "Brevo"}` : `Saved ${addEmail} (pending — assign to a list)`);
      setAddName(""); setAddEmail("");
      loadSubscribers();
    } catch (e) {
      notify("error", e.message || "Could not add contact");
    } finally {
      setAddingContact(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) return notify("error", "Enter a test email address");
    if (!html) return notify("error", "Generate a flyer first");
    setSendingTest(true);
    try {
      const res = await fetch(`${API}/admin/marketing/test`, { method: "POST", headers: jsonHeaders(), body: JSON.stringify({ to_email: testEmail, subject, html: currentHtml(), attachment_url: attachPdf ? priceListUrl : null }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Send failed");
      notify("success", `Test email sent to ${testEmail}`);
    } catch (e) {
      notify("error", e.message || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const sendCampaign = async () => {
    setConfirmSend(false);
    if (!listId) return notify("error", "Pick a contact list");
    if (!html || !subject) return notify("error", "Generate a flyer first");
    setSending(true);
    try {
      const list = lists.find((l) => String(l.id) === String(listId));
      const res = await fetch(`${API}/admin/marketing/send`, {
        method: "POST", headers: jsonHeaders(),
        body: JSON.stringify({ name: `${(TEMPLATES.find((tp) => tp.id === templateId) || {}).name || "Email"} — ${new Date().toLocaleDateString()}`, subject, html: currentHtml(), list_id: Number(listId), list_name: list?.name || "", attachment_url: attachPdf ? priceListUrl : null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Send failed");
      notify("success", "Campaign sent successfully!");
      loadHistory();
    } catch (e) {
      notify("error", e.message || "Campaign send failed");
    } finally {
      setSending(false);
    }
  };

  const selectedList = lists.find((l) => String(l.id) === String(listId));

  return (
    <div data-testid="admin-marketing">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-slate-800 flex items-center gap-2">
          <Wand2 className="w-6 h-6 text-green-600" /> Marketing Studio
        </h1>
        <p className="text-slate-500">Turn your inventory into polished email campaigns and send them via Brevo — pick a template, no AI credits needed.</p>
      </div>

      {/* Website signup list setting */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-signup-settings">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h2 className="font-bold text-slate-800 flex items-center gap-2"><Inbox className="w-5 h-5 text-green-600" /> Website signup list</h2>
            <p className="text-sm text-slate-500 mt-1">Visitors who subscribe on your website are added to this Brevo list.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Brevo list</label>
              <select value={signupListId} onChange={(e) => setSignupListId(e.target.value)} className="form-input w-auto min-w-[220px]" data-testid="signup-list-select">
                <option value="">— None (capture only) —</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.subscriber_count} contacts)</option>)}
              </select>
            </div>
            <button onClick={saveSettings} disabled={savingSettings} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60" data-testid="signup-list-save">
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
        {listError && <p className="mt-3 text-sm text-amber-700">Brevo lists unavailable: {listError}</p>}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-500">
            <strong className="text-slate-700">Drip campaigns &amp; welcome series</strong> (automated multi-step emails) are built in Brevo's Automation studio.
          </p>
          <a href="https://app.brevo.com/automation/list" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 font-bold text-sm hover:border-green-400 shrink-0" data-testid="brevo-automations-link">
            <Zap className="w-4 h-4 text-green-600" /> Open Brevo Automations
          </a>
        </div>
      </section>

      {/* Subscribers manager */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-subscribers">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" /> Website Subscribers
              <span className="text-sm font-semibold text-slate-400">({subscribers.length})</span>
              {subPending > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{subPending} pending</span>}
            </h2>
            <p className="text-sm text-slate-500 mt-1">People who signed up on your website. Select and assign them to a Brevo list (single or all).</p>
          </div>
        </div>

        {/* Manual add contact */}
        <div className="mb-4 p-4 rounded-xl bg-green-50/60 border border-green-200" data-testid="marketing-add-contact">
          <p className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3"><UserPlus className="w-4 h-4 text-green-600" /> Add a contact manually</p>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <label className="text-xs font-semibold text-slate-500 block mb-1">Name (optional)</label>
              <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Jane Doe" className="form-input" data-testid="add-contact-name" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-slate-500 block mb-1">Email</label>
              <input type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addContact()} placeholder="jane@company.com" className="form-input" data-testid="add-contact-email" />
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs font-semibold text-slate-500 block mb-1">Add to list (optional)</label>
              <select
                value={addListId}
                onChange={(e) => {
                  if (e.target.value === "__new__") { setShowNewList(true); setAddListId(""); }
                  else setAddListId(e.target.value);
                }}
                className="form-input"
                data-testid="add-contact-list"
              >
                <option value="">No list (save as pending)</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                <option value="__new__">➕ Create new list…</option>
              </select>
            </div>
            <button onClick={addContact} disabled={addingContact || !addEmail.trim()} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60" data-testid="add-contact-btn">
              {addingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
            </button>
          </div>
          {showNewList && (
            <div className="mt-3 flex items-end gap-2 flex-wrap" data-testid="new-list-row">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-semibold text-slate-500 block mb-1">New list name</label>
                <input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createList()}
                  placeholder="e.g. Wholesale Buyers"
                  className="form-input"
                  data-testid="new-list-name"
                  autoFocus
                />
              </div>
              <button onClick={createList} disabled={creatingList || !newListName.trim()} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm disabled:opacity-60" data-testid="new-list-create-btn">
                {creatingList ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create list
              </button>
              <button onClick={() => { setShowNewList(false); setNewListName(""); }} className="px-3 py-2.5 rounded-lg border border-slate-300 text-slate-600 font-semibold text-sm" data-testid="new-list-cancel-btn">
                Cancel
              </button>
            </div>
          )}
        </div>

        {subscribers.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No subscribers yet. They'll appear here when visitors join your mailing list.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="subscribers-assign-bar">
              <span className="text-sm font-bold text-slate-600">{selectedSubs.length} selected</span>
              <select value={assignListId} onChange={(e) => setAssignListId(e.target.value)} className="form-input w-auto py-1.5" data-testid="assign-list-select">
                <option value="">Choose a list…</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.subscriber_count})</option>)}
              </select>
              <button onClick={() => assignSubs(false)} disabled={assigning || !assignListId || selectedSubs.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-50" data-testid="assign-selected-btn">
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />} Assign selected
              </button>
              <button onClick={() => assignSubs(true)} disabled={assigning || !assignListId} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 font-bold text-sm hover:border-green-400 disabled:opacity-50" data-testid="assign-all-btn">
                Assign all ({subscribers.length})
              </button>
            </div>

            <div className="overflow-x-auto border border-slate-100 rounded-xl max-h-[340px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                  <tr>
                    <th className="px-3 py-2.5 w-10"><input type="checkbox" checked={allSubsSelected} onChange={toggleAllSubs} className="w-4 h-4 accent-green-600" data-testid="subscribers-select-all" /></th>
                    <th className="px-3 py-2.5 font-semibold">Name</th>
                    <th className="px-3 py-2.5 font-semibold">Email</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id} className={`border-t border-slate-100 ${selectedSubs.includes(s.id) ? "bg-green-50/50" : ""}`} data-testid={`subscriber-row-${s.id}`}>
                      <td className="px-3 py-2.5"><input type="checkbox" checked={selectedSubs.includes(s.id)} onChange={() => toggleSub(s.id)} className="w-4 h-4 accent-green-600" data-testid={`subscriber-select-${s.id}`} /></td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{s.name || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{s.email}</td>
                      <td className="px-3 py-2.5">
                        {s.synced ? (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{s.list_name || "Assigned"}</span>
                        ) : (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => deleteSub(s.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50" data-testid={`subscriber-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Deliverability — bad / bounced / unsubscribed contacts */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-deliverability">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="text-lg font-extrabold text-slate-800 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-600" /> Email Deliverability
              {deliver && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700" data-testid="deliverability-count">
                  {deliver.blocked_count} bad
                </span>
              )}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Addresses that bounced, unsubscribed or were blocked. Brevo auto-suppresses these from future campaigns — review and clean them here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadDeliverability} disabled={deliverLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 font-bold text-sm hover:border-green-400 disabled:opacity-50" data-testid="deliverability-refresh-btn">
              {deliverLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
            </button>
            <button onClick={exportBadEmails} disabled={!deliver || deliver.blocked_count === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm disabled:opacity-40" data-testid="deliverability-export-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {deliver && (
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <Stat label="Bad / blocked" value={deliver.blocked_count} accent="text-rose-600" />
            <Stat label="Contacts scanned" value={deliver.scanned} />
            <Stat label="Total in Brevo" value={deliver.total_contacts} />
          </div>
        )}

        {deliverLoading && !deliver ? (
          <p className="text-sm text-slate-400 py-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking Brevo…</p>
        ) : !deliver || deliver.blocked_count === 0 ? (
          <p className="text-sm text-slate-400 py-4" data-testid="deliverability-empty">
            🎉 No bad emails — your list is clean. Bounced or unsubscribed addresses will show up here after your campaigns.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm" data-testid="deliverability-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold">Email</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Reason</th>
                  <th className="px-3 py-2.5 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {deliver.blocked.map((b) => (
                  <tr key={b.email} className="border-t border-slate-100" data-testid={`deliverability-row-${b.email}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-700">{b.email}</td>
                    <td className="px-3 py-2.5 text-slate-500">{[b.first, b.last].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">{b.reason}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button onClick={() => removeBadContact(b.email)} className="text-slate-400 hover:text-rose-600" title="Delete contact from Brevo" data-testid={`deliverability-delete-${b.email}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <StepBadge n={1} label="Choose template" active done={!!templateId} />
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <StepBadge n={2} label="Pick products" active={!!templateId} done={selected.length > 0} />
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <StepBadge n={3} label="Edit & preview" active={selected.length > 0} done={!!html} />
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <StepBadge n={4} label="Send" active={!!html} />
      </div>

      {/* Step 1: Choose a template */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-template-gallery">
        <h2 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
          <LayoutTemplate className="w-5 h-5 text-green-600" /> 1. Choose a template
          <span className="text-xs font-normal text-slate-400">(no AI credits used)</span>
        </h2>
        <p className="text-sm text-slate-500 mb-4">Pick a layout — you'll choose products and customize the look in the next steps.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => setTemplateId(tpl.id)}
              data-testid={`template-${tpl.id}`}
              className={`text-left rounded-xl border-2 p-3 transition-all ${templateId === tpl.id ? "border-green-500 ring-2 ring-green-500/20 bg-green-50/50" : "border-slate-200 hover:border-green-300"}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-800">{tpl.name}</span>
                {templateId === tpl.id && <CheckCircle2 className="w-4 h-4 text-green-600" />}
              </div>
              <p className="text-xs text-slate-400 mt-1 leading-snug">{tpl.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Step 2: Pick products */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
        <h2 className="font-bold text-slate-800 mb-4">2. Pick products to feature <span className="text-green-600">({selected.length})</span></h2>
        {/* Source toggle */}
        <div className="inline-flex bg-slate-100 rounded-full p-1 mb-5">
          <button onClick={() => switchSource("inventory")} className={`px-4 py-2 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${source === "inventory" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="source-inventory">
            <Database className="w-4 h-4" /> Live Inventory
          </button>
          <button onClick={() => switchSource("upload")} className={`px-4 py-2 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${source === "upload" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="source-upload">
            <Upload className="w-4 h-4" /> Upload a Sheet
          </button>
        </div>

        {source === "upload" && parsed.length === 0 && (
          <SheetSource notify={notify} onLoaded={onSheetLoaded} />
        )}

        {(source === "inventory" || parsed.length > 0) && (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="text-sm text-slate-500">
                {source === "upload" && (
                  <button onClick={() => { setParsed([]); setSelected([]); }} className="text-sm font-semibold text-slate-400 hover:text-slate-600 underline">change file</button>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-semibold text-slate-500">Language</span>
                <select value={lang} onChange={(e) => setLang(e.target.value)} className="form-input w-auto" data-testid="marketing-lang">
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
            </div>

            {source === "upload" && parsed.length > 0 && (
              <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 flex items-start gap-2" data-testid="marketing-upload-note">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-600">
                  Loaded <strong className="text-slate-800">{parsed.length} products</strong> from your sheet — all selected for this email. These are <strong>only used for this campaign and are NOT added to your inventory.</strong> Untick any you don't want below.
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="marketing-picker-toolbar">
              {source === "inventory" && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="marketing-stage-filter">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Show:</span>
                  {[
                    ["finished", "Finished", products.filter((p) => (p.stage || "finished") === "finished").length],
                    ["in_process", "In Process", products.filter((p) => p.stage === "in_process").length],
                    ["all", "All", products.length],
                  ].map(([val, label, count]) => (
                    <button
                      key={val}
                      onClick={() => setMktStage(val)}
                      data-testid={`marketing-stage-${val}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 border transition-colors ${
                        mktStage === val ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-green-400"
                      }`}
                    >
                      {label}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${mktStage === val ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={pickSearch}
                  onChange={(e) => setPickSearch(e.target.value)}
                  placeholder="Search products to feature…"
                  className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-300 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 text-sm"
                  data-testid="marketing-picker-search"
                />
              </div>
              <select value={pickCat} onChange={(e) => setPickCat(e.target.value)} className="form-input w-auto py-2 text-sm" data-testid="marketing-picker-category">
                <option value="all">All categories</option>
                {CATEGORY_SLUGS.map((s) => <option key={s} value={s}>{categoryLabel(s, "en")}</option>)}
              </select>
              {selected.length > 0 && (
                <button onClick={() => setSelected([])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm" data-testid="marketing-clear-selected">
                  <X className="w-3.5 h-3.5" /> {selected.length} selected · Clear
                </button>
              )}
              <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="marketing-picker-view">
                <button onClick={() => setPickView("list")} data-testid="marketing-pick-list" className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${pickView === "list" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>
                  <ListIcon className="w-4 h-4" /> List
                </button>
                <button onClick={() => setPickView("grid")} data-testid="marketing-pick-grid" className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${pickView === "grid" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>
                  <LayoutGrid className="w-4 h-4" /> Grid
                </button>
              </div>
            </div>

            {pool.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No products match this filter.</p>
            ) : pickView === "list" ? (
              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[380px] overflow-y-auto" data-testid="marketing-picker-list">
                <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 sticky top-0 z-10 text-xs font-bold text-slate-500 uppercase tracking-wide">
                  <input
                    type="checkbox"
                    checked={pool.length > 0 && pool.every((p) => selected.includes(p.id))}
                    onChange={(e) => {
                      const ids = pool.map((p) => p.id);
                      setSelected((s) => e.target.checked ? Array.from(new Set([...s, ...ids])) : s.filter((id) => !ids.includes(id)));
                    }}
                    className="w-4 h-4 accent-green-600"
                    data-testid="marketing-pick-selectall"
                  />
                  <span className="flex-1">Product</span>
                  <span className="hidden sm:block w-28">Category</span>
                  <span className="w-20 text-right">Qty</span>
                </div>
                {pool.map((p) => {
                  const on = selected.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      data-testid={`marketing-product-${p.id}`}
                      className={`flex items-center gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer transition-colors ${on ? "bg-green-50" : "hover:bg-slate-50"}`}
                    >
                      <input type="checkbox" checked={on} onChange={() => toggle(p.id)} className="w-4 h-4 accent-green-600" data-testid={`marketing-pick-${p.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
                          {p.name}
                          {source === "inventory" && p.stage === "in_process" && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">In Process</span>
                          )}
                        </div>
                        {(p.brand || p.model) && <div className="text-xs text-slate-400 truncate">{[p.brand, p.model].filter(Boolean).join(" · ")}</div>}
                      </div>
                      <span className="hidden sm:block w-28 text-xs text-slate-500 truncate">{categoryLabel(p.category, "en")}</span>
                      <span className="w-20 text-right text-sm font-bold text-slate-800">{p.quantity != null && p.quantity !== "" ? p.quantity : "—"}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-h-[380px] overflow-y-auto pr-1">
                {pool.map((p) => {
                  const on = selected.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => toggle(p.id)} data-testid={`marketing-product-${p.id}`}
                      className={`text-left rounded-xl border-2 overflow-hidden transition-all ${on ? "border-green-500 ring-2 ring-green-500/20" : "border-slate-200 hover:border-green-300"}`}>
                      <div className="relative aspect-[4/3] bg-slate-100">
                        <img src={resolveImage(p.image_url, p.category)} alt="" className="w-full h-full object-cover" />
                        {on && <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-green-600 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-white" /></span>}
                        {source === "inventory" && p.stage === "in_process" && (
                          <span className="absolute bottom-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">In Process</span>
                        )}
                      </div>
                      <div className="p-2">
                        <div className="text-xs font-bold text-slate-800 truncate">{p.name}</div>
                        <div className="text-xs text-slate-400">{p.quantity != null && p.quantity !== "" ? `${p.quantity} in stock` : categoryLabel(p.category, "en")}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {generating && (
              <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm" data-testid="marketing-generating-hint">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                <span>Building your email…</span>
              </div>
            )}

            {selectedProducts.length > 0 && (
              <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="marketing-selected-strip">
                <p className="text-xs font-bold text-slate-500 mb-2">FLYER ORDER — {selectedProducts.length} selected (reorder with arrows)</p>
                <div className="flex flex-col gap-1.5 max-h-[260px] overflow-y-auto">
                  {selectedProducts.map((p, idx) => (
                    <div key={p.id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-2 py-1.5" data-testid={`selected-item-${p.id}`}>
                      <span className="text-xs font-mono text-slate-400 w-5">{idx + 1}</span>
                      <img src={resolveImage(p.image_url, p.category)} alt="" className="w-8 h-8 rounded object-cover" />
                      <span className="text-sm font-semibold text-slate-700 truncate flex-1">{p.name}</span>
                      <span className="text-xs text-slate-400">{p.quantity != null && p.quantity !== "" ? `${p.quantity}` : ""}</span>
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`move-up-${p.id}`}><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === selectedProducts.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`move-down-${p.id}`}><ArrowDown className="w-4 h-4" /></button>
                      <button onClick={() => removeSel(p.id)} className="p-1 text-slate-400 hover:text-red-500" data-testid={`remove-sel-${p.id}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Build */}
            <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-testid="marketing-build-row">
              <p className="text-sm text-slate-500">
                Using the <strong className="text-slate-700">{(TEMPLATES.find((tp) => tp.id === templateId) || {}).name}</strong> template.
                <button onClick={() => document.querySelector('[data-testid="marketing-template-gallery"]')?.scrollIntoView({ behavior: "smooth" })} className="ml-2 text-green-600 font-semibold underline">change template</button>
              </p>
              <button
                onClick={buildEmail}
                disabled={selected.length === 0}
                data-testid="marketing-build-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60"
              >
                <Wand2 className="w-4 h-4" /> Build email ({selected.length} {selected.length === 1 ? "product" : "products"})
              </button>
            </div>
          </>
        )}
      </section>

      {/* Step 2: editor + preview */}
      {html && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-editor">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-bold text-slate-800">3. Edit & preview your email</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex bg-slate-100 rounded-full p-0.5">
                <button onClick={() => changeView("preview")} className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${view === "preview" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="marketing-view-preview">
                  <Eye className="w-4 h-4" /> Preview
                </button>
                <button onClick={() => changeView("edit")} className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${view === "edit" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="marketing-view-edit">
                  <Pencil className="w-4 h-4" /> Edit text
                </button>
                <button onClick={() => changeView("code")} className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${view === "code" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="marketing-view-code">
                  <Code2 className="w-4 h-4" /> HTML
                </button>
              </div>
              <button onClick={() => setFullPreview(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 font-bold text-sm hover:border-green-400" data-testid="marketing-fullpreview-btn">
                <Maximize2 className="w-4 h-4" /> Full preview
              </button>
              <button onClick={() => setShowBrand((s) => !s)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-bold text-sm ${showBrand ? "border-green-500 text-green-700 bg-green-50" : "border-slate-300 text-slate-600 hover:border-green-400"}`} data-testid="marketing-brand-toggle">
                <Palette className="w-4 h-4" /> Logo & colors
              </button>
              <button onClick={saveDraft} disabled={savingDraft} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 font-bold text-sm hover:border-green-400 disabled:opacity-60" data-testid="marketing-save-draft-btn">
                {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
              </button>
              <button onClick={buildEmail} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 font-bold text-sm hover:border-green-400" data-testid="marketing-rebuild-btn">
                <RefreshCw className="w-4 h-4" /> Rebuild from template
              </button>
            </div>
          </div>

          {showBrand && (
            <div className="mb-4 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="marketing-brand-panel">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-sm font-bold text-slate-700 flex items-center gap-2"><Palette className="w-4 h-4 text-green-600" /> Logo & email colors</p>
                <button onClick={resetBrand} className="text-xs font-semibold text-slate-400 hover:text-slate-600 underline" data-testid="marketing-brand-reset">Reset to defaults</button>
              </div>
              <p className="text-xs text-slate-400 mb-3">Changes apply to the preview instantly. The logo auto-switches to a white version on dark headers.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Logo style</label>
                  <select value={brand.logo_style} onChange={(e) => restyleBrand("logo_style", e.target.value)} className="form-input" data-testid="brand-logo-style">
                    <option value="auto">Auto (match header)</option>
                    <option value="dark">Dark logo</option>
                    <option value="white">White logo</option>
                  </select>
                </div>
                <ColorField label="Header background" testid="brand-header-bg" value={brand.header_bg} onChange={(v) => restyleBrand("header_bg", v)} />
                <ColorField label="Accent / buttons" testid="brand-accent" value={brand.accent_color} onChange={(v) => restyleBrand("accent_color", v)} />
                <ColorField label="Body text" testid="brand-text" value={brand.text_color} onChange={(v) => restyleBrand("text_color", v)} />
                <ColorField label="Footer background" testid="brand-footer-bg" value={brand.footer_bg} onChange={(v) => restyleBrand("footer_bg", v)} />
                <ColorField label="Footer text" testid="brand-footer-text" value={brand.footer_text} onChange={(v) => restyleBrand("footer_text", v)} />
              </div>
            </div>
          )}

          {productImages.length > 0 && (
            <div className="mb-4 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="marketing-image-swap">
              <p className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3"><ImageIcon className="w-4 h-4 text-green-600" /> Swap pictures on this email</p>
              <div className="flex gap-3 flex-wrap">
                {productImages.map((src, i) => (
                  <div key={i} className="relative group" data-testid={`flyer-image-${i}`}>
                    <img src={src} alt="" className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
                    <button onClick={() => { setSwapSrc(src); setSwapTab("upload"); setSwapResults([]); setSwapUrl(""); setSwapQuery(""); }} className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-bold" data-testid={`flyer-image-change-${i}`}>
                      Change
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="text-sm font-semibold text-slate-700 block mb-1">Subject line</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="form-input" data-testid="marketing-subject" />
          </div>

          {view === "edit" && (
            <div className="mb-2 flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <Pencil className="w-4 h-4 shrink-0" />
              Click directly on any text or headline below to edit it. Switch to Preview when done.
            </div>
          )}

          {view === "code" ? (
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              spellCheck={false}
              data-testid="marketing-html"
              className="w-full h-[520px] rounded-xl border border-slate-200 p-4 font-mono text-xs text-slate-700 outline-none focus:ring-2 focus:ring-green-500/30"
            />
          ) : (
            <iframe
              title="flyer-preview"
              srcDoc={html}
              ref={previewRef}
              onLoad={applyMode}
              data-testid="marketing-preview-frame"
              className={`w-full h-[520px] rounded-xl border bg-white ${view === "edit" ? "border-green-400 ring-2 ring-green-500/20" : "border-slate-200"}`}
            />
          )}
        </section>
      )}

      {/* Step 3: send */}
      {html && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-send">
          <h2 className="font-bold text-slate-800 mb-4">4. Send</h2>

          {listError && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="marketing-list-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Brevo lists unavailable: {listError}</span>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Send test to yourself first</label>
              <div className="flex gap-2">
                <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="form-input flex-1" data-testid="marketing-test-email" />
                <button onClick={sendTest} disabled={sendingTest} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-bold text-sm hover:border-green-400 disabled:opacity-60 shrink-0" data-testid="marketing-send-test-btn">
                  {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />} Test
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Contact list</label>
              <select value={listId} onChange={(e) => setListId(e.target.value)} className="form-input" data-testid="marketing-list-select">
                {lists.length === 0 && <option value="">No lists available</option>}
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.subscriber_count} contacts)</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm text-slate-500">From: <span className="font-semibold text-slate-700">{sender?.email || "sales@tele-comp.com"}</span></p>
              <label className="mt-2 inline-flex items-center gap-2 cursor-pointer" data-testid="marketing-attach-pdf-label">
                <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} className="w-4 h-4 accent-green-600" data-testid="marketing-attach-pdf" />
                <span className="text-sm font-semibold text-slate-700 inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-green-600" /> Attach inventory list (PDF)</span>
              </label>
            </div>
            <button onClick={() => setConfirmSend(true)} disabled={sending || !listId || lists.length === 0} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold disabled:opacity-60" data-testid="marketing-send-campaign-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Campaign
            </button>
          </div>
        </section>
      )}

      {/* Drafts */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="marketing-drafts">
        <h2 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-green-600" /> Saved Drafts
        </h2>
        {drafts.length === 0 ? (
          <p className="text-sm text-slate-400">No drafts yet. Generate a flyer and click "Save draft" to keep it here.</p>
        ) : (
          <div className="space-y-2">
            {drafts.map((d) => (
              <div key={d.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-testid={`draft-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{d.name || d.subject || "Untitled"}</div>
                  <div className="text-xs text-slate-400">Updated {new Date(d.updated_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openDraft(d.id)} className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm" data-testid={`open-draft-${d.id}`}>Open</button>
                  <button onClick={() => deleteDraft(d.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50" data-testid={`delete-draft-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* History + stats */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-green-600" /> Sent Campaigns
        </h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400">No campaigns sent yet.</p>
        ) : (
          <div className="space-y-3">
            {history.map((c) => {
              const st = stats[c.id];
              return (
                <div key={c.id} className="rounded-xl border border-slate-100 p-3" data-testid={`campaign-${c.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{c.subject}</div>
                      <div className="text-xs text-slate-400">{c.list_name || `List ${c.list_id}`} · {new Date(c.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c.status === "sent" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{c.status}</span>
                      {c.status === "sent" && c.brevo_campaign_id && (
                        <>
                          <button onClick={() => loadStats(c)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 font-semibold text-xs hover:border-green-400" data-testid={`stats-btn-${c.id}`}><BarChart2 className="w-3.5 h-3.5" /> Stats</button>
                          <button onClick={() => resend(c)} disabled={resending === c.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 font-semibold text-xs hover:border-green-400 disabled:opacity-60" data-testid={`resend-btn-${c.id}`}>{resending === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5" />} Resend</button>
                        </>
                      )}
                    </div>
                  </div>
                  {st && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3 pt-3 border-t border-slate-100" data-testid={`stats-${c.id}`}>
                      <Stat label="Delivered" value={st.delivered} />
                      <Stat label="Opens" value={st.uniqueViews} accent />
                      <Stat label="Clicks" value={st.uniqueClicks} accent />
                      <Stat label="Bounces" value={(st.softBounces || 0) + (st.hardBounces || 0)} />
                      <Stat label="Unsub" value={st.unsubscriptions} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {confirmSend && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/70" onClick={() => setConfirmSend(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="marketing-confirm">
            <h3 className="text-lg font-extrabold text-slate-800 flex items-center gap-2"><Mail className="w-5 h-5 text-green-600" /> Send campaign?</h3>
            <p className="text-slate-500 mt-2">This will email <span className="font-bold text-slate-700">"{subject}"</span> to <span className="font-bold text-slate-700">{selectedList?.name}</span> ({selectedList?.subscriber_count} contacts).</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmSend(false)} className="px-5 py-2.5 rounded-lg border border-slate-300 font-semibold text-slate-600">Cancel</button>
              <button onClick={sendCampaign} className="px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="marketing-confirm-send-btn">Send now</button>
            </div>
          </div>
        </div>
      )}

      {/* Full-page preview */}
      {fullPreview && (
        <div className="fixed inset-0 z-[140] bg-slate-900/80 flex flex-col" data-testid="marketing-fullpreview-modal">
          <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200">
            <h3 className="font-extrabold text-slate-800 flex items-center gap-2"><Eye className="w-5 h-5 text-green-600" /> Full preview <span className="text-sm font-normal text-slate-400 truncate max-w-[40vw]">— {subject}</span></h3>
            <div className="flex items-center gap-2">
              <button onClick={() => { const w = window.open("", "_blank"); if (w) { w.document.write(html); w.document.close(); } }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 font-bold text-sm hover:border-green-400" data-testid="marketing-fullpreview-newtab">
                Open in new tab
              </button>
              <button onClick={() => setFullPreview(false)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="marketing-fullpreview-close"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-slate-200 p-4">
            <iframe title="full-preview" srcDoc={html} className="w-full h-full min-h-[80vh] bg-white rounded-lg border-0" data-testid="marketing-fullpreview-frame" />
          </div>
        </div>
      )}

      {/* Image swap modal */}
      {swapSrc && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/70" onClick={() => setSwapSrc(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="image-swap-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <h3 className="font-extrabold text-slate-800 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-green-600" /> Change picture</h3>
              <button onClick={() => setSwapSrc(null)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="image-swap-close"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <img src={swapSrc} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                <span className="text-sm text-slate-500">Replacing this image. Pick a new one below.</span>
              </div>
              <div className="inline-flex bg-slate-100 rounded-full p-1 mb-4">
                {[["upload", "Upload", UploadCloud], ["url", "Paste URL", Link2], ["search", "Web search", Search]].map(([id, label, Icon]) => (
                  <button key={id} onClick={() => setSwapTab(id)} data-testid={`image-swap-tab-${id}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold inline-flex items-center gap-1.5 ${swapTab === id ? "bg-white shadow text-slate-800" : "text-slate-500"}`}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>

              {swapTab === "upload" && (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-10 cursor-pointer hover:border-green-400" data-testid="image-swap-upload-label">
                  {swapBusy ? <Loader2 className="w-6 h-6 animate-spin text-green-600" /> : <UploadCloud className="w-7 h-7 text-slate-400" />}
                  <span className="text-sm font-semibold text-slate-600">Click to choose an image</span>
                  <input type="file" accept="image/*" className="hidden" data-testid="image-swap-file" onChange={(e) => uploadSwapFile(e.target.files?.[0])} />
                </label>
              )}

              {swapTab === "url" && (
                <div className="flex gap-2">
                  <input value={swapUrl} onChange={(e) => setSwapUrl(e.target.value)} placeholder="https://…/image.jpg" className="form-input flex-1" data-testid="image-swap-url-input" />
                  <button onClick={useSwapUrl} disabled={swapBusy || !swapUrl.trim()} className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60" data-testid="image-swap-url-btn">
                    {swapBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Use"}
                  </button>
                </div>
              )}

              {swapTab === "search" && (
                <div>
                  <div className="flex gap-2 mb-3">
                    <input value={swapQuery} onChange={(e) => setSwapQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runImageSearch()} placeholder="e.g. Dell OptiPlex 7050" className="form-input flex-1" data-testid="image-swap-search-input" />
                    <button onClick={runImageSearch} disabled={swapBusy || !swapQuery.trim()} className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60" data-testid="image-swap-search-btn">
                      {swapBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[320px] overflow-y-auto">
                    {swapResults.map((img, i) => (
                      <button key={i} onClick={() => pickSearchResult(img.url)} disabled={swapBusy} className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 hover:border-green-500" data-testid={`image-swap-result-${i}`}>
                        <img src={img.thumbnail} alt={img.title} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  );
}
